/**
 * Floating Reel Animation - Connected Train/Snake Version
 * All clips are positioned along the path at once, flowing together
 * Like cars on a road or a train following tracks
 */

import gsap from 'gsap'
import { MotionPathPlugin } from 'gsap/MotionPathPlugin'

gsap.registerPlugin(MotionPathPlugin)

/* ========================================
   ANIMATION CONFIGURATION
   Tweak these parameters to control the animation behavior
   ======================================== */
const CONFIG = {
  // Opacity Settings (applied to both stroke and frames)
  opacity: {
    baseOpacity: 0.4,      // Base opacity for both stroke and frames (0-1)
    fadeInStart: 0.24,     // Where frames start fading in (0-1 along path)
    fadeInEnd: 0.30,       // Where frames finish fading in (0-1 along path)
    fadeOutStart: 0.9,     // Where frames start fading out (0-1 along path)
  },

  // Stroke Width
  stroke: {
    startWidth: 4,         // Width at start of stroke (px)
    endWidth: 15,          // Width at end of stroke (px)
  },

  // Path Control Points (as fractions of viewport width/height)
  // Path has 3 main segments: left-to-right rise, right-to-left descend, left-to-middle
  path: {
    // Segment 1: Left to Right (rising curve)
    segment1: {
      startX: 0.08,        // Start X position (fraction of viewport width)
      controlX1: 0.12,     // First control point X
      controlX2: 0.22,     // Second control point X
      midX: 0.35,          // Mid point X
      controlX3: 0.5,      // Third control point X
      controlX4: 0.65,     // Fourth control point X
      endX: 0.88,          // End X position

      heightStart: 0.02,   // Height offset from start (gentler slope)
      heightMid1: 0.05,    // Height at first mid point (much gentler)
      heightMid2: 0.09,    // Height at second mid point (reduced)
      heightMid3: 0.13,    // Height at third mid point (reduced)
      heightMid4: 0.16,    // Height at fourth mid point (reduced)
      heightEnd: 0.18,     // Height at end of segment 1 (reduced)
    },

    // Segment 2: Right to Left (descending curve)
    segment2: {
      controlX1: 0.88,     // First control point X
      controlX2: 0.75,     // Second control point X
      midX: 0.6,           // Mid point X
      controlX3: 0.42,     // Third control point X
      controlX4: 0.28,     // Fourth control point X
      endX: 0.1,           // End X position

      heightStart: 0.23,   // Height offset from path start (reduced)
      heightMid1: 0.31,    // Height at first mid point (reduced)
      heightMid2: 0.38,    // Height at second mid point (reduced)
      heightMid3: 0.43,    // Height at third mid point (reduced)
      heightMid4: 0.47,    // Height at fourth mid point (reduced)
      heightEnd: 0.50,     // Height at end of segment 2 (reduced)
    },

    // Segment 3: Left back to Middle (final curve)
    segment3: {
      controlX1: 0.2,      // First control point X
      controlX2: 0.35,     // Second control point X
      endX: 0.5,           // End X position (middle of screen)

      heightStart: 0.53,   // Height offset from path start (adjusted to match segment 2 end)
      heightMid: 0.62,     // Height at mid point (reduced)
      heightEnd: 0.65,     // Final height (reduced)
    },
  },

  // Animation Timing
  timing: {
    frameDuration: 60,     // Duration for frames to complete path (seconds)
    gradientDuration: 8,   // Duration for stroke gradient animation (seconds)
  },

  // Frame Settings
  frames: {
    count: 20,             // Number of frames in the animation (reduced for better performance)
  },

  // Performance Settings
  performance: {
    pauseWhenOffScreen: true,  // Pause animations when hero section is not visible
    reducedFrameCount: 20,     // Optional: lower frame count for better performance
  },
}

interface FilmFrame {
  gradient: string
  width: number
}

export function initReelAnimation() {
  const background = document.getElementById('aboutBackground')
  if (!background) return

  // Define colorful gradients from design system
  const gradients = [
    'linear-gradient(135deg, #325EFC 0%, #47DBFF 100%)', // Blue to Cyan
    'linear-gradient(135deg, #EC6696 0%, #FFDD00 100%)', // Magenta to Yellow
    'linear-gradient(135deg, #3ADFD8 0%, #325EFC 100%)', // Teal to Blue
    'linear-gradient(135deg, #47DBFF 0%, #3ADFD8 100%)', // Cyan to Teal
    'linear-gradient(135deg, #FFDD00 0%, #EC6696 100%)', // Yellow to Magenta
    'linear-gradient(135deg, #325EFC 0%, #EC6696 100%)', // Blue to Magenta
    'linear-gradient(135deg, #3ADFD8 0%, #FFDD00 100%)', // Teal to Yellow
    'linear-gradient(135deg, #EC6696 0%, #325EFC 100%)', // Magenta to Blue
  ]

  const clipCount = CONFIG.frames.count

  // Width distribution: each entry is [width, probability]
  const widthDistribution = [
    { width: 10, probability: 0.15 },
    { width: 30, probability: 0.20 },
    { width: 60, probability: 0.20 },
    { width: 90, probability: 0.20 },
    { width: 120, probability: 0.15 },
    { width: 150, probability: 0.10 }
  ]

  // Create SVG and path elements
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  svg.style.cssText = 'position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none;'

  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
  path.setAttribute('id', 'reelPath')
  path.setAttribute('stroke', 'none')
  path.setAttribute('fill', 'none')

  // Create a filled polygon for variable-width stroke effect
  const taperedStroke = document.createElementNS('http://www.w3.org/2000/svg', 'path')
  taperedStroke.setAttribute('fill', 'url(#lineGradient)')
  taperedStroke.setAttribute('mask', 'url(#strokeFadeMask)')

  // Create gradient for the stroke with spreadMethod for infinite tiling
  const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs')
  const gradient = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient')
  gradient.setAttribute('id', 'lineGradient')
  gradient.setAttribute('x1', '0%')
  gradient.setAttribute('y1', '0%')
  gradient.setAttribute('x2', '50%')  // Larger range to spread out colors more
  gradient.setAttribute('y2', '50%')
  gradient.setAttribute('spreadMethod', 'repeat')  // Make gradient tile infinitely

  // Single color cycle pattern
  const stop1 = document.createElementNS('http://www.w3.org/2000/svg', 'stop')
  stop1.setAttribute('offset', '0%')
  stop1.setAttribute('style', 'stop-color:#325EFC;stop-opacity:1') // Blue

  const stop2 = document.createElementNS('http://www.w3.org/2000/svg', 'stop')
  stop2.setAttribute('offset', '25%')
  stop2.setAttribute('style', 'stop-color:#3ADFD8;stop-opacity:1') // Teal

  const stop3 = document.createElementNS('http://www.w3.org/2000/svg', 'stop')
  stop3.setAttribute('offset', '50%')
  stop3.setAttribute('style', 'stop-color:#EC6696;stop-opacity:1') // Magenta

  const stop4 = document.createElementNS('http://www.w3.org/2000/svg', 'stop')
  stop4.setAttribute('offset', '75%')
  stop4.setAttribute('style', 'stop-color:#FFDD00;stop-opacity:1') // Yellow

  const stop5 = document.createElementNS('http://www.w3.org/2000/svg', 'stop')
  stop5.setAttribute('offset', '100%')
  stop5.setAttribute('style', 'stop-color:#325EFC;stop-opacity:1') // Back to Blue

  gradient.appendChild(stop1)
  gradient.appendChild(stop2)
  gradient.appendChild(stop3)
  gradient.appendChild(stop4)
  gradient.appendChild(stop5)
  defs.appendChild(gradient)

  // Animate the gradient - now seamless because spreadMethod repeats the pattern
  const gradientProps = { x1: 0, y1: 0, x2: 50, y2: 50 }
  const gradientAnimation = gsap.to(gradientProps, {
    x1: 50,
    y1: 50,
    x2: 100,
    y2: 100,
    duration: CONFIG.timing.gradientDuration,
    repeat: -1,
    ease: 'none',
    onUpdate: () => {
      gradient.setAttribute('x1', `${gradientProps.x1}%`)
      gradient.setAttribute('y1', `${gradientProps.y1}%`)
      gradient.setAttribute('x2', `${gradientProps.x2}%`)
      gradient.setAttribute('y2', `${gradientProps.y2}%`)
    }
  })

  // Create mask for fade effect at end of stroke
  const strokeFadeMask = document.createElementNS('http://www.w3.org/2000/svg', 'mask')
  strokeFadeMask.setAttribute('id', 'strokeFadeMask')

  const maskPath = document.createElementNS('http://www.w3.org/2000/svg', 'path')
  maskPath.setAttribute('id', 'strokeMaskPath')
  // Will be populated with same geometry as taperedStroke

  strokeFadeMask.appendChild(maskPath)
  defs.appendChild(strokeFadeMask)

  // Create mask gradient once (will be updated in updatePath)
  const maskGradient = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient')
  maskGradient.setAttribute('id', 'maskFadeGradient')
  maskGradient.setAttribute('gradientUnits', 'userSpaceOnUse')

  const maskStop1 = document.createElementNS('http://www.w3.org/2000/svg', 'stop')
  maskStop1.setAttribute('offset', '0%')
  maskStop1.setAttribute('stop-color', 'white')
  maskStop1.setAttribute('stop-opacity', '0')

  const maskStop2 = document.createElementNS('http://www.w3.org/2000/svg', 'stop')
  maskStop2.setAttribute('offset', '15%')
  maskStop2.setAttribute('stop-color', 'white')
  maskStop2.setAttribute('stop-opacity', String(CONFIG.opacity.baseOpacity))

  const maskStop3 = document.createElementNS('http://www.w3.org/2000/svg', 'stop')
  maskStop3.setAttribute('offset', '60%')
  maskStop3.setAttribute('stop-color', 'white')
  maskStop3.setAttribute('stop-opacity', String(CONFIG.opacity.baseOpacity))

  const maskStop4 = document.createElementNS('http://www.w3.org/2000/svg', 'stop')
  maskStop4.setAttribute('offset', '100%')
  maskStop4.setAttribute('stop-color', 'white')
  maskStop4.setAttribute('stop-opacity', '0')

  maskGradient.appendChild(maskStop1)
  maskGradient.appendChild(maskStop2)
  maskGradient.appendChild(maskStop3)
  maskGradient.appendChild(maskStop4)
  defs.appendChild(maskGradient)

  svg.appendChild(defs)
  svg.appendChild(path)
  svg.appendChild(taperedStroke)

  background.appendChild(svg)

  // Function to update path based on about page container size
  function updatePath() {
    const viewportWidth = window.innerWidth
    const aboutSection = background!.closest('.about-page') as HTMLElement | null
    const fullSectionHeight = aboutSection ? aboutSection.offsetHeight : window.innerHeight

    // Get the container from the about page section
    const referenceContainer = aboutSection?.querySelector('.container') as HTMLElement | null

    if (!referenceContainer) {
      // Fallback to viewport if container not found
      svg.setAttribute('width', String(viewportWidth))
      svg.setAttribute('height', String(fullSectionHeight))
      return
    }

    // Calculate animation height to end before the CTA section
    const ctaSection = aboutSection?.querySelector('.about-page__cta') as HTMLElement | null
    let containerHeight: number

    if (ctaSection && aboutSection) {
      // Get the position of CTA relative to the about section
      const aboutRect = aboutSection.getBoundingClientRect()
      const ctaRect = ctaSection.getBoundingClientRect()
      const ctaOffsetFromTop = ctaRect.top - aboutRect.top

      // End animation just before CTA (use 95% of distance to CTA for smooth ending)
      containerHeight = ctaOffsetFromTop * 0.95
    } else {
      // Fallback: use 70% of full height if CTA not found
      containerHeight = fullSectionHeight * 0.7
    }

    // Get container's bounding rect to calculate actual content area matching other sections
    const containerRect = referenceContainer.getBoundingClientRect()
    const containerWidth = containerRect.width

    // Use container width for animation to align with cards in other sections
    const animationWidth = containerWidth

    // Center the animation within the viewport to align with container
    const xOffset = containerRect.left

    // SVG covers full section height, but animation path uses calculated containerHeight
    svg.setAttribute('width', String(viewportWidth))
    svg.setAttribute('height', String(fullSectionHeight))

    // Get path start position from CSS variable
    const pathStartY = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--reel-path-start-y'))
    const startY = pathStartY / containerHeight

    const s1 = CONFIG.path.segment1
    const s2 = CONFIG.path.segment2
    const s3 = CONFIG.path.segment3

    const pathData = `M ${xOffset + animationWidth * s1.startX} ${containerHeight * startY}
      C ${xOffset + animationWidth * s1.controlX1} ${containerHeight * (startY + s1.heightStart)}, ${xOffset + animationWidth * s1.controlX2} ${containerHeight * (startY + s1.heightMid1)}, ${xOffset + animationWidth * s1.midX} ${containerHeight * (startY + s1.heightMid2)}
      C ${xOffset + animationWidth * s1.controlX3} ${containerHeight * (startY + s1.heightMid3)}, ${xOffset + animationWidth * s1.controlX4} ${containerHeight * (startY + s1.heightMid4)}, ${xOffset + animationWidth * s1.endX} ${containerHeight * (startY + s1.heightEnd)}
      C ${xOffset + animationWidth * s2.controlX1} ${containerHeight * (startY + s2.heightStart)}, ${xOffset + animationWidth * s2.controlX2} ${containerHeight * (startY + s2.heightMid1)}, ${xOffset + animationWidth * s2.midX} ${containerHeight * (startY + s2.heightMid2)}
      C ${xOffset + animationWidth * s2.controlX3} ${containerHeight * (startY + s2.heightMid3)}, ${xOffset + animationWidth * s2.controlX4} ${containerHeight * (startY + s2.heightMid4)}, ${xOffset + animationWidth * s2.endX} ${containerHeight * (startY + s2.heightEnd)}
      C ${xOffset + animationWidth * s3.controlX1} ${containerHeight * (startY + s3.heightStart)}, ${xOffset + animationWidth * s3.controlX2} ${containerHeight * (startY + s3.heightMid)}, ${xOffset + animationWidth * s3.endX} ${containerHeight * (startY + s3.heightEnd)}`
    path.setAttribute('d', pathData)

    // Create tapered stroke as filled polygon
    const pathLength = path.getTotalLength()
    const segmentLength = pathLength * 0.25 // First 1/4 of path
    const samples = 50 // Number of points to sample

    const leftEdge: Array<{x: number, y: number}> = []
    const rightEdge: Array<{x: number, y: number}> = []

    for (let i = 0; i <= samples; i++) {
      const distance = (i / samples) * segmentLength
      const point = path.getPointAtLength(distance)

      // Get tangent by sampling nearby points
      const tangentStep = 2
      const nextDist = Math.min(distance + tangentStep, segmentLength)
      const prevDist = Math.max(distance - tangentStep, 0)
      const nextPoint = path.getPointAtLength(nextDist)
      const prevPoint = path.getPointAtLength(prevDist)
      const dx = nextPoint.x - prevPoint.x
      const dy = nextPoint.y - prevPoint.y
      const len = Math.sqrt(dx * dx + dy * dy) || 1 // Avoid division by zero

      // Perpendicular normal (rotated 90 degrees)
      const nx = -dy / len
      const ny = dx / len

      // Width: varies from start to end
      const progress = i / samples
      const halfWidth = CONFIG.stroke.startWidth + (progress * (CONFIG.stroke.endWidth - CONFIG.stroke.startWidth))

      leftEdge.push({ x: point.x + nx * halfWidth, y: point.y + ny * halfWidth })
      rightEdge.push({ x: point.x - nx * halfWidth, y: point.y - ny * halfWidth })
    }

    // Build polygon path: start -> left edge -> end -> right edge (reversed) -> back to start
    let polygonPath = `M ${leftEdge[0].x} ${leftEdge[0].y}`
    for (let i = 1; i < leftEdge.length; i++) {
      polygonPath += ` L ${leftEdge[i].x} ${leftEdge[i].y}`
    }
    for (let i = rightEdge.length - 1; i >= 0; i--) {
      polygonPath += ` L ${rightEdge[i].x} ${rightEdge[i].y}`
    }
    polygonPath += ' Z'

    taperedStroke.setAttribute('d', polygonPath)

    // Update mask gradient coordinates based on new stroke geometry
    // Use actual stroke start/end points for better alignment
    const startPoint = leftEdge[0]
    const endPoint = leftEdge[leftEdge.length - 1]

    maskGradient.setAttribute('x1', String(startPoint.x))
    maskGradient.setAttribute('y1', String(startPoint.y))
    maskGradient.setAttribute('x2', String(endPoint.x))
    maskGradient.setAttribute('y2', String(endPoint.y))

    // Apply same path to mask with gradient fill
    maskPath.setAttribute('d', polygonPath)
    maskPath.setAttribute('fill', 'url(#maskFadeGradient)')
  }

  // Initialize path
  updatePath()

  // Create all clips
  const clips: HTMLElement[] = []
  const animations: gsap.core.Tween[] = []
  let resizeTimeout: number

  for (let i = 0; i < clipCount; i++) {
    // Select width based on probability distribution
    const random = Math.random()
    let cumulativeProbability = 0
    let width = widthDistribution[0].width // Default to first width

    for (const { width: w, probability } of widthDistribution) {
      cumulativeProbability += probability
      if (random < cumulativeProbability) {
        width = w
        break
      }
    }

    const frame: FilmFrame = {
      gradient: gradients[i % gradients.length],
      width,
    }

    const element = createFilmFrame(frame)
    background.appendChild(element)
    clips.push(element)

    // Position each clip along the path based on its index
    // This distributes them evenly along the entire path
    const progress = i / clipCount

    // Calculate initial rotation based on position
    let initialRotateY, initialRotateX
    if (progress < 0.5) {
      const t = progress * 2
      initialRotateY = 0
      initialRotateX = 90 - (90 * t)  // 90 to 0 (edge to full face)
    } else {
      const t = (progress - 0.5) * 2
      initialRotateY = 0
      initialRotateX = 0 - (90 * t)   // 0 to -90 (full face to edge)
    }

    // Use GSAP MotionPath to position along path
    gsap.set(element, {
      motionPath: {
        path: '#reelPath',
        align: '#reelPath',
        alignOrigin: [0.5, 0.5],
        autoRotate: false,
        start: progress,
        end: progress,
      },
      rotateY: initialRotateY,
      rotateX: initialRotateX,
    })

    // Set initial opacity using CONFIG values
    let opacity = 0
    if (progress >= CONFIG.opacity.fadeInStart) {
      if (progress < CONFIG.opacity.fadeInEnd) {
        const fadeRange = CONFIG.opacity.fadeInEnd - CONFIG.opacity.fadeInStart
        opacity = (progress - CONFIG.opacity.fadeInStart) / fadeRange * CONFIG.opacity.baseOpacity
      } else if (progress > CONFIG.opacity.fadeOutStart) {
        const fadeRange = 1 - CONFIG.opacity.fadeOutStart
        opacity = (1 - progress) / fadeRange * CONFIG.opacity.baseOpacity
      } else {
        opacity = CONFIG.opacity.baseOpacity
      }
    }
    gsap.set(element, { opacity })
  }

  // Animate all clips together along the path
  clips.forEach((element, index) => {
    const startProgress = index / clipCount

    const tween = gsap.to(element, {
      motionPath: {
        path: '#reelPath',
        align: '#reelPath',
        alignOrigin: [0.5, 0.5],
        autoRotate: false,
        start: startProgress,
        end: startProgress + 1, // Move full length of path
      },
      duration: CONFIG.timing.frameDuration,
      repeat: -1,
      ease: 'none', // Linear movement for consistent flow
      onUpdate: function() {
        // Get current progress (0-1 along the path)
        const currentProgress = (this.progress() + startProgress) % 1

        // Calculate height based on position along path
        // Start (0): 7.5px (25% of 30px)
        // Middle (0.5): 60px (200% of 30px)
        // End (1): 7.5px (25% of 30px)
        let heightMultiplier
        if (currentProgress < 0.5) {
          // Growing from 0.25x to 2x (first half of path)
          heightMultiplier = 0.25 + (currentProgress * 2) * 1.75
        } else {
          // Shrinking from 2x to 0.25x (second half of path)
          heightMultiplier = 2 - ((currentProgress - 0.5) * 2) * 1.75
        }

        const height = 30 * heightMultiplier
        element.style.height = `${height}px`

        // Calculate 3D rotation based on position
        // Top-left: showing bottom edge as line (rotateX = 90, rotateY = 0)
        // Middle-right: fully facing - full rectangle visible (rotateX = 0, rotateY = 0)
        // Bottom-left: showing top edge as line (rotateX = -90, rotateY = 0)
        let rotateY, rotateX

        if (currentProgress < 0.5) {
          // First half: from bottom edge to full front
          const t = currentProgress * 2 // 0 to 1
          rotateY = 0  // No Y rotation
          rotateX = 90 - (90 * t)    // 90 to 0 (edge to full face)
        } else {
          // Second half: from full front to top edge
          const t = (currentProgress - 0.5) * 2 // 0 to 1
          rotateY = 0  // No Y rotation
          rotateX = 0 - (90 * t)    // 0 to -90 (full face to edge)
        }

        // Apply 3D transformation
        gsap.set(element, {
          rotateY,
          rotateX,
        })

        // Handle opacity using CONFIG values
        let opacity = 0
        if (currentProgress >= CONFIG.opacity.fadeInStart) {
          // Fade in
          if (currentProgress < CONFIG.opacity.fadeInEnd) {
            const fadeRange = CONFIG.opacity.fadeInEnd - CONFIG.opacity.fadeInStart
            opacity = (currentProgress - CONFIG.opacity.fadeInStart) / fadeRange * CONFIG.opacity.baseOpacity
          } else if (currentProgress > CONFIG.opacity.fadeOutStart) {
            // Fade out at the end
            const fadeRange = 1 - CONFIG.opacity.fadeOutStart
            opacity = (1 - currentProgress) / fadeRange * CONFIG.opacity.baseOpacity
          } else {
            opacity = CONFIG.opacity.baseOpacity
          }
        }
        gsap.set(element, { opacity })
      }
    })

    // Store animation reference for resize handling
    animations.push(tween)
  })

  // Update animations when path changes on resize
  function refreshAnimations() {
    animations.forEach(tween => {
      tween.invalidate()
    })
  }

  // Handle window resize: update path and refresh animations
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimeout)
    resizeTimeout = window.setTimeout(() => {
      updatePath()
      refreshAnimations()
    }, 150)
  })

  // Performance optimization: Pause animations when about page is not visible
  if (CONFIG.performance.pauseWhenOffScreen) {
    // Observe the background div itself (where animations live) not the entire about page
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // About page is visible - resume all animations
          gradientAnimation.play()
          animations.forEach(tween => tween.play())
        } else {
          // About page is not visible - pause all animations to save resources
          gradientAnimation.pause()
          animations.forEach(tween => tween.pause())
        }
      })
    }, {
      // Trigger as soon as ANY part of the animation area is visible/hidden
      threshold: 0,
      // No extra margin - pause immediately when off screen
      rootMargin: '0px'
    })

    observer.observe(background)
  }
}

function createFilmFrame(frame: FilmFrame): HTMLElement {
  const element = document.createElement('div')
  element.className = 'film-frame'

  element.style.cssText = `
    width: ${frame.width}px;
  `

  // Create inner content with gradient
  const content = document.createElement('div')
  content.className = 'film-frame__content'
  content.style.background = frame.gradient

  element.appendChild(content)

  return element
}
